import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { VisualHierarchyItem } from "../components/visual-hierarchy-item";
import { DroppableWrapper, SortableItem } from "@/components/ui/drag-drop-sort";
import { useVisualHierarchy } from "../hooks/use-visual-hierarchy";
import { useSegment } from "@/hooks/use-segment";

/* Prop Types */
interface SetVisualHierarchyModalProps {
  projectId: string; // used for segment tracking
  studyId: string; // used for segment tracking
  onClose: () => void;
}
/*
 * SetVisualHierarchyModal component
 * This component is used to set visual hierarchy priorities for regions
 */
export const SetVisualHierarchyModal = ({ onClose, projectId, studyId }: SetVisualHierarchyModalProps) => {
  // Custom hook to manage visual hierarchy state and actions
  const { priorityColumns, activeId, handleDragStart, handleDragOver, handleDragEnd, handleAddPriorityLevel, handleRemovePriorityLevel, handleSetPriority } = useVisualHierarchy({
    projectId,
    studyId,
  });

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Sensors */
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /* Handlers */
  const handleFinish = () => {
    // Segment tracking
    segmentTrackEvent("AOI Priority Editing Finished", { projectId, studyId });

    // Save the visual hierarchy changes
    handleSetPriority();

    // Close the modal
    onClose();
  };

  const handleCancel = () => {
    // Segment tracking
    segmentTrackEvent("AOI Priority Editing Cancelled", { projectId, studyId });

    // close the modal without saving changes
    onClose();
  };

  /* Render */
  return (
    <div className="flex grow flex-col gap-4 h-full max-h-[80vh] overflow-hidden">
      <div className="flex justify-between items-center">
        <h1>Set priorities</h1>
        <div>
          <Button onClick={handleCancel} className="mr-2">
            Cancel
          </Button>
          <Button variant={"primary"} onClick={handleFinish}>
            Finish
          </Button>
        </div>
      </div>
      <div className="flex flex-row gap-2 justify-between items-center">
        <p className="opacity-70 w-1/2">
          You can set your desired priorities by assigning an AOI to a priority group.{" "}
          <a href="https://academy.dragonflyai.co/en/articles/11886551-priority-alignment" target="_blank" rel="noopener noreferrer" className="underline">
            Learn&nbsp;more
          </a>
        </p>
        <div className="flex gap-2 items-center">
          <p className="mr-4">Priority levels</p>
          <Button onClick={handleRemovePriorityLevel} disabled={priorityColumns.length === 2} className="w-fit text-sm font-semibold mr-2">
            <MinusIcon className="size-5" />
          </Button>
          <Button onClick={handleAddPriorityLevel} disabled={priorityColumns.length === 5} className="w-fit text-sm font-semibold">
            <PlusIcon className="size-5" />
          </Button>
        </div>
      </div>
      <DndContext onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragStart={handleDragStart} sensors={sensors} collisionDetection={closestCenter}>
        <div className="flex flex-row gap-4 mt-4 grow overflow-auto">
          {priorityColumns.map((column) => (
            <SortableContext id={column.id} key={column.id} items={column.items.map((item) => `${item?.label}`)} strategy={verticalListSortingStrategy}>
              <div key={column.id} className="flex flex-col w-full overflow-auto h-full">
                <DroppableWrapper id={column.id} className={`${column.id === "0" ? "bg-base-100" : "bg-base-200"} p-4 rounded-lg flex flex-col grow overflow-auto`}>
                  <h2>{column.title}</h2>
                  <p className="text-sm opacity-70 mb-4">{column.desc}</p>
                  {column.items.filter(Boolean).map((item) => (
                    <SortableItem id={item.label} key={item.label}>
                      <VisualHierarchyItem id={item.label} columnsState={priorityColumns} className={item.label === activeId ? "opacity-40" : ""} />
                    </SortableItem>
                  ))}
                </DroppableWrapper>
              </div>
            </SortableContext>
          ))}
          {/* We have to use a portal due to the modal */}
          {createPortal(<DragOverlay>{activeId ? <VisualHierarchyItem id={activeId} columnsState={priorityColumns} /> : null}</DragOverlay>, document.body)}
        </div>
      </DndContext>
    </div>
  );
};
